/* External */
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

/* Utils */
import useRequestReducer from '../../hooks/useRequestReducer';
import { useIsForegroundApp } from '../ForegroundAppProvider';
import {
  clearSavedInterstitial,
  getInterstitial,
  getSavedInterstitial,
  mergePrevAndNewInterstitial,
  saveInterstitial,
  shouldDisplayNewInterstitial,
} from './utils';
import { InterstitialContext } from './contexts';
import { useIsLoadingUserAccount, useIsLoggedIn } from '../UserProvider';

function InterstitialProvider({ children }) {
  const {
    state: {
      isLoading,
      response: interstitial,
    },
    performRequest,
  } = useRequestReducer(getInterstitial, mergePrevAndNewInterstitial);
  const interstitialRef = useRef(interstitial);
  const isForegroundApp = useIsForegroundApp();
  const isLoggedIn = useIsLoggedIn();
  const isLoadingUser = useIsLoadingUserAccount();
  const [oldIntersitital, setOldInterstitial] = useState(() => getSavedInterstitial());

  const shouldDisplay = useMemo(
    () => shouldDisplayNewInterstitial(oldIntersitital, interstitial),
    [oldIntersitital, interstitial],
  );

  const handleInterstitialSeen = useCallback(() => {
    setOldInterstitial(interstitialRef.current);
    saveInterstitial(interstitialRef.current);
  }, []);

  useEffect(() => {
    if (!isLoadingUser) {
      if (isLoggedIn) {
        if (isForegroundApp) {
          performRequest();
        }
      } else {
        setOldInterstitial(undefined);
        clearSavedInterstitial();
      }
    }
  }, [isForegroundApp, isLoadingUser, isLoggedIn, performRequest]);

  useEffect(() => {
    interstitialRef.current = interstitial;
  }, [interstitial]);

  const contextValue = useMemo(() => ({
    interstitial,
    isLoading,
    shouldDisplay,
    handleInterstitialSeen,
  }), [
    handleInterstitialSeen,
    interstitial,
    isLoading,
    shouldDisplay,
  ]);

  return (
    <InterstitialContext.Provider value={contextValue}>
      {children}
    </InterstitialContext.Provider>
  );
}

InterstitialProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default memo(InterstitialProvider);
